<script setup lang="ts">
import ChannelIcon from '@/components/channels/ChannelIcon.vue'
import CommunityBottomSheet from '@/components/common/CommunityBottomSheet.vue'
import FillLockIcon from '@/components/common/icons/FillLockIcon.vue'
import { GroupState } from '@/composition/groups'
import { isMobile } from '@/util/helper'
import { UITextLgMedium, UITextMdNormal } from '@gohighlevel/ghl-ui'
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface Position {
  left: number
  top: number
}
interface Channel {
  _id: string
  name: string
  icon?: string
}

const show = ref(false)
const showBottomSheet = ref(false)
const position = ref<Position>({ left: 0, top: 0 })
const channelToShow = ref<Channel | null>(null)
const channelName = ref('')
const { t } = useI18n()

function handleShowTooltip(event: CustomEvent) {
  const channel = GroupState?.state?.channelList?.find(
    channel => channel._id === event?.detail?.channelId
  )
  channelToShow.value = channel || null
  if (!channel) {
    channelName.value = event?.detail?.channelName
  }

  if (isMobile()) {
    // Show bottom sheet on mobile
    showBottomSheet.value = true
  } else {
    // Show tooltip on desktop
    if (show.value) {
      show.value = false
    } else {
      position.value.left = event?.detail?.parent?.left
      position.value.top = event?.detail?.parent?.top - 40

      if (!channelToShow.value) {
        position.value.top = event?.detail?.parent?.top - 40
      }
      show.value = true
    }
  }
}

function handleHideTooltip() {
  if (isMobile()) {
    showBottomSheet.value = false
  } else if (show.value) {
    show.value = false
  }
}

onMounted(() => {
  const tooltipElement = document.getElementById('channel-details-tooltip')
  if (tooltipElement) {
    tooltipElement.addEventListener(
      'show-private-channel-tooltip',
      handleShowTooltip as EventListener
    )
    tooltipElement.addEventListener(
      'close-private-channel-tooltip',
      handleHideTooltip
    )
  }

  window.addEventListener('scroll', handleHideTooltip)
  window.addEventListener('wheel', handleHideTooltip)
})

onUnmounted(() => {
  const tooltipElement = document.getElementById('channel-details-tooltip')
  tooltipElement?.removeEventListener(
    'show-private-channel-tooltip',
    handleShowTooltip as EventListener
  )
  tooltipElement?.removeEventListener(
    'close-private-channel-tooltip',
    handleHideTooltip
  )
  window.removeEventListener('scroll', handleHideTooltip)
  window.removeEventListener('wheel', handleHideTooltip)
})
</script>

<template>
  <teleport to="body">
    <div
      v-show="show && !isMobile()"
      id="channel-details-tooltip"
      role="tooltip"
      class="fixed max-w-[304px] overflow-hidden rounded-lg border border-solid border-communities-border bg-communities-background p-1 px-2 text-center shadow-lg"
      :style="{
        top: position.top + 'px',
        left: position.left + 'px',
        zIndex: 3000,
      }"
    >
      <div
        v-if="channelToShow"
        class="flex items-center gap-[2px] text-xs font-semibold text-communities-font-primary"
      >
        <div class="mr-[2px]">
          <ChannelIcon
            :icon-size="16"
            :emoji-size="16"
            :channel="channelToShow"
            :showing-in-sidebar="false"
            :show-lock-icon="false"
          />
        </div>
        {{ channelToShow?.name }}
      </div>
      <div
        v-else
        class="flex items-center text-xs font-semibold text-communities-font-primary"
      >
        <FillLockIcon
          class="h-5 w-5 text-communities-font-primary"
          :fill="'var(--fontPrimary)'"
        />
        {{ t('channel.privateChannelTooltip') }}
      </div>
    </div>
  </teleport>

  <!-- Mobile Bottom Sheet -->
  <CommunityBottomSheet
    v-if="isMobile()"
    v-model="showBottomSheet"
    :is-full-screen="false"
    :is-drag-handle="true"
    :is-scroll-lock="true"
    :snap-point="'auto'"
    :is-backdrop="true"
  >
    <div class="px-6 pb-8 pt-4">
      <UITextLgMedium
        class="channel-name flex w-fit items-center gap-0.5 rounded p-0.5 text-base font-normal not-italic no-underline"
      >
        <FillLockIcon
          class="h-5 w-5 text-communities-font-primary"
          :fill="'var(--fontPrimary)'"
        />
        {{ channelName || t('channel.privateChannel') }}
      </UITextLgMedium>
      <UITextMdNormal class="mr-3 text-communities-font-secondary">
        {{ t('channel.privateChannelTooltip') }}
      </UITextMdNormal>
    </div>
  </CommunityBottomSheet>
</template>

<style scoped>
.community-bottom-sheet {
  z-index: 200 !important;
}
.swipe-modal::backdrop {
  backdrop-filter: blur(1px) !important;
}
.channel-name {
  color: var(--tertiary);
  background-color: var(--tertiaryLight) !important;
  border: 0.3px solid var(--sidebar-selected-item-font) !important;
}
</style>
